'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { BarChart3, BrainCircuit, CheckCircle2, ChevronRight, Database, FileCheck, Landmark, Link2, Lock, MessageCircle, Shield, Sparkles, TrendingUp, Users, Zap, X, Building2, type LucideIcon } from 'lucide-react';
import { GlassCard } from '@/components/ui/glass-card';
import featureModalContent, { type FeatureModalData, type ModalSectionData } from './featureModalContent';
const iconMap: Record<string, LucideIcon> = {
  TrendingUp,
  Users,
  Zap,
  BarChart3,
  Link2,
  FileCheck,
  Database,
  Shield,
  Lock,
  Landmark,
  CheckCircle2,
  BrainCircuit,
  MessageCircle,
  Sparkles,
  Building2
};
interface Feature {
  title: string;
  description: string;
  icon: string;
  gradient: string;
}
interface FeatureCardsGridProps {
  features: Feature[];
}
export default function FeatureCardsGrid(t0) {
  const $ = _c(10);
  const {
    features
  } = t0;
  const [activeModal, setActiveModal] = useState(null);
  let t1;
  if ($[0] !== features) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = feature => {
        const FeatureIcon = iconMap[feature.icon] || Zap;
        const modalData = featureModalContent[feature.title];
        return <GlassCard key={feature.title} className="p-8 group hover:scale-[1.02] transition-all cursor-pointer border border-white/10 hover:border-cyan-500/30" onClick={modalData ? () => setActiveModal(modalData) : undefined}><div className={`inline-flex p-3 rounded-2xl bg-gradient-to-br ${feature.gradient} mb-6`}><FeatureIcon className="h-6 w-6 text-white" /></div><h3 className="text-xl font-semibold mb-3 text-white group-hover:text-cyan-400 transition-colors">{feature.title}</h3><p className="text-white/60">{feature.description}</p><div className="mt-6 flex items-center text-sm text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity">Learn more <ChevronRight className="h-4 w-4 ml-1" /></div></GlassCard>;
      };
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = features.map(t2);
    $[0] = features;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== activeModal) {
    t3 = activeModal && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setActiveModal(null)}><div className="absolute inset-0 bg-black/70 backdrop-blur-sm" /><div className="relative w-full max-w-3xl max-h-[85vh] overflow-y-auto glass-strong rounded-2xl border border-cyan-500/20 shadow-2xl animate-in fade-in zoom-in-95 duration-200" onClick={_temp}><div className="sticky top-0 z-10 flex items-start justify-between p-6 pb-4 border-b border-white/10 glass-strong rounded-t-2xl"><div className="flex items-center gap-3"><div className={`inline-flex p-2.5 rounded-xl bg-gradient-to-br ${activeModal.gradient}`}>{(() => {
                const Icon = iconMap[activeModal.icon] || Zap;
                return <Icon className="h-5 w-5 text-white" />;
              })()}</div><h2 className="text-xl font-bold text-white">{activeModal.modalTitle}</h2></div><button onClick={() => setActiveModal(null)} className="text-white/50 hover:text-white transition-colors p-1"><X className="h-5 w-5" /></button></div><div className="p-6 space-y-8"><div className="space-y-4">{activeModal.intro.map(_temp2)}</div>{activeModal.sections.map(_temp3)}</div></div></div>;
    $[5] = activeModal;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}

/* ── Modal section helper ─────────────────────────────────────────────── */
function _temp3(section) {
  return <ModalSection key={section.title} {...section} />;
}
function _temp2(paragraph) {
  return <p key={paragraph.slice(0, 40)} className="text-white/70 leading-relaxed">{paragraph}</p>;
}
function _temp(e) {
  return e.stopPropagation();
}
function ModalSection(t0) {
  const $ = _c(14);
  const {
    title,
    items,
    bullets,
    paragraphs
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="h-1 w-6 rounded-full bg-gradient-to-r from-cyan-400 to-teal-400" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <h3 className="text-lg font-semibold text-white flex items-center gap-2">{t1}{title}</h3>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== items) {
    t3 = items && <div className="space-y-3">{items.map(_temp4)}</div>;
    $[3] = items;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== paragraphs) {
    t4 = paragraphs && paragraphs.map(_temp5);
    $[5] = paragraphs;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== bullets) {
    t5 = bullets && <div className="space-y-2">{bullets.map(_temp6)}</div>;
    $[7] = bullets;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = <div className="space-y-4">{t2}{t3}{t4}{t5}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp6(bullet) {
  return <div key={bullet} className="flex items-start gap-3"><CheckCircle2 className="h-4 w-4 text-cyan-400 flex-shrink-0 mt-0.5" /><p className="text-white/70 text-sm">{bullet}</p></div>;
}
function _temp5(p) {
  return <p key={p.slice(0, 40)} className="text-white/70 leading-relaxed text-sm">{p}</p>;
}
function _temp4(item) {
  return <div key={item.heading} className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" /><div><p className="text-white font-medium">{item.heading}</p><p className="text-white/60 text-sm">{item.text}</p></div></div>;
}